import createHttp from '@/config/http';
import type {
	CreateDraftPayload,
	Draft,
	DraftResponse,
	DraftsResponse,
	UpdateDraftPayload,
} from '@/types/draft';

const http = createHttp(import.meta.env.VITE_COMMON_URL);

/**
 * Create a new draft
 */
export const createDraft = async (
	payload: CreateDraftPayload,
): Promise<Draft> => {
	const response = await http.post<DraftResponse>('/drafts', payload);
	return response.data.data as Draft;
};

/**
 * Get all drafts for a user with optional filters
 */
export const getDrafts = async (
	userId: string,
	page = 1,
	limit = 15,
	search = '',
	columnFilters: any[] = [],
): Promise<{
	items: Draft[];
	meta: DraftsResponse['data']['metaData'];
}> => {
	const response = await http.get<DraftsResponse>('/drafts', {
		params: {
			userId,
			page,
			limit,
			...(search && { search }),
			...(columnFilters.length > 0 && { columnFilters: JSON.stringify(columnFilters) }),
		},
	});
	return {
		items: response.data.data.items,
		meta: response.data.data.metaData,
	};
};

/**
 * Get the most recent draft for a specific form type
 */
export const getLatestDraft = async (
	userId: string,
	formType: string,
): Promise<Draft | null> => {
	const response = await http.get<DraftResponse>('/drafts/latest', {
		params: {
			userId,
			formType,
		},
	});
	return response.data.data;
};

/**
 * Get a single draft by ID
 */
export const getDraftById = async (id: string): Promise<Draft> => {
	const response = await http.get<DraftResponse>(`/drafts/${id}`);
	return response.data.data as Draft;
};

/**
 * Update an existing draft
 */
export const updateDraft = async (
	id: string,
	payload: UpdateDraftPayload,
): Promise<Draft> => {
	const response = await http.put<DraftResponse>(`/drafts/${id}`, payload);
	return response.data.data as Draft;
};

/**
 * Delete a draft
 */
export const deleteDraft = async (id: string): Promise<{ _id: string }> => {
	const response = await http.delete<{ data: { _id: string } }>(
		`/drafts/${id}`,
	);
	return response.data.data;
};
